import * as React from 'react';
import { cn } from '../lib/utils';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  hint?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, hint, id, ...props }, ref) => (
    <div className="w-full">
      <input
        ref={ref}
        id={id}
        className={cn(
          'flex h-10 w-full rounded-md-token border border-border bg-surface px-3 text-sm text-text',
          'placeholder:text-text-muted',
          'transition-colors transition-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-0',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-60',
          error && 'border-danger focus-visible:ring-danger',
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...props}
      />
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1.5 text-xs text-text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
Input.displayName = 'Input';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  hint?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, hint, id, ...props }, ref) => (
    <div className="w-full">
      <textarea
        ref={ref}
        id={id}
        className={cn(
          'flex min-h-[100px] w-full rounded-md-token border border-border bg-surface px-3 py-2 text-sm text-text',
          'placeholder:text-text-muted',
          'transition-colors transition-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-60',
          error && 'border-danger focus-visible:ring-danger',
          className,
        )}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      {hint && !error && (
        <p className="mt-1.5 text-xs text-text-muted">{hint}</p>
      )}
      {error && (
        <p className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
Textarea.displayName = 'Textarea';

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, error, children, ...props }, ref) => (
    <div className="w-full">
      <select
        ref={ref}
        className={cn(
          'flex h-10 w-full rounded-md-token border border-border bg-surface px-3 text-sm text-text',
          'transition-colors transition-fast',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-60',
          error && 'border-danger',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  ),
);
Select.displayName = 'Select';
